// src/components/SettingsDialog.js
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Box,
  Typography,
} from '@mui/material';
import usePreferences from '../contexts/PreferencesContext.js';

export default function SettingsDialog({ open, onClose }) {
  const isProd = process.env.NODE_ENV === 'production';

  // ─── Always call hooks first ──────────────────────────────────────
  const {
    modality,
    setModality,
    apiKey,
    setApiKey,
    model,
    setModel,
  } = usePreferences();

  const [tempModality, setTempModality]     = useState(modality);
  const [tempApiKey,    setTempApiKey]      = useState(apiKey);
  const [tempModel,     setTempModel]       = useState(model);
  const [cloudModels,   setCloudModels]     = useState([]);
  const [localModels,   setLocalModels]     = useState([]);
  const [loadingModels, setLoadingModels]   = useState(false);
  const [fetchError,    setFetchError]      = useState('');

  // Reset temp state when dialog opens
  useEffect(() => {
    if (open) {
      setTempModality(modality);
      setTempApiKey(apiKey);
      setTempModel(model);
    }
  }, [open, modality, apiKey, model]);

  // Fetch available models whenever modality or API key changes
  useEffect(() => {
    if (!open) return;
    setLoadingModels(true);
    setFetchError('');

    if (tempModality === 'Cloud') {
      if (!(tempApiKey?.trim?.())) {
        setCloudModels([]);
        setTempModel('');
        setFetchError('Please enter your OpenAI API key');
        setLoadingModels(false);
        return;
      }
      fetch('https://api.openai.com/v1/models', {
        headers: { Authorization: `Bearer ${tempApiKey}` },
      })
        .then(res => {
          if (!res.ok) throw new Error(`Status ${res.status}`);
          return res.json();
        })
        .then(json => {
          const ids = (json.data || [])
            .map(m => m.id)
            .sort((a, b) => a.localeCompare(b));
          setCloudModels(ids);
          if (!ids.includes(tempModel)) setTempModel(ids[0] || '');
        })
        .catch(err => {
          const msg = err.message.includes('401')
            ? 'Invalid OpenAI API key'
            : 'Failed to fetch OpenAI models';
          setFetchError(msg);
          setCloudModels([]);
          setTempModel('');
        })
        .finally(() => setLoadingModels(false));
    } else {
      fetch('http://localhost:11434/api/tags')
        .then(res => {
          if (!res.ok) throw new Error(`Status ${res.status}`);
          return res.json();
        })
        .then(json => {
          const names = (Array.isArray(json.models) ? json.models : [])
            .map(m => m.name || m.model)
            .sort((a, b) => a.localeCompare(b));
          setLocalModels(names);
          if (!names.includes(tempModel)) setTempModel(names[0] || '');
        })
        .catch(err => {
          console.error('Failed to fetch local models:', err);
          setLocalModels([]);
          setTempModel('');
        })
        .finally(() => setLoadingModels(false));
    }
  }, [open, tempModality, tempApiKey, tempModel]);

  // Hide entire dialog in production
  if (isProd) {
    return null;
  }

  // ─── Build menu options & handlers ──────────────────────────────
  const base = tempModality === 'Cloud' ? cloudModels : localModels;
  const menuOptions = ['', ...base];
  if (tempModel && !menuOptions.includes(tempModel)) {
    menuOptions.push(tempModel);
  }
  const safeValue = menuOptions.includes(tempModel) ? tempModel : '';
  const disableSelect = loadingModels || !!fetchError;

  const handleSave = () => {
    setModality(tempModality);
    setApiKey(tempApiKey);
    setModel(tempModel);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent dividers>
        {!model && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#fff4e5', border: '1px solid #ffd58f', borderRadius: 1 }}>
            <Typography variant="body2">
              Please choose a model to continue.
            </Typography>
          </Box>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel shrink id="modality-label">Modality</InputLabel>
          <Select
            labelId="modality-label"
            value={tempModality}
            onChange={e => {
              setTempModality(e.target.value);
              setTempModel('');
            }}
            label="Modality"
          >
            <MenuItem value="Cloud">Cloud</MenuItem>
            <MenuItem value="Local">Local</MenuItem>
          </Select>
        </FormControl>

        {tempModality === 'Cloud' && (
          <Box>
            <TextField
              label="OpenAI API Key"
              fullWidth
              margin="normal"
              type="password"
              value={tempApiKey}
              onChange={e => setTempApiKey(e.target.value)}
            />
            {fetchError && <FormHelperText error>{fetchError}</FormHelperText>}
          </Box>
        )}

        <FormControl fullWidth margin="normal" error={!!fetchError}>
          <InputLabel shrink id="model-label">Model</InputLabel>
          <Select
            labelId="model-label"
            value={safeValue}
            onChange={e => setTempModel(e.target.value)}
            label="Model"
            disabled={disableSelect}
          >
            <MenuItem value=""><em>— none —</em></MenuItem>
            {base.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
            {tempModel && !base.includes(tempModel) && (
              <MenuItem key={tempModel} value={tempModel}>{tempModel}</MenuItem>
            )}
            {loadingModels && (
              <MenuItem value="" disabled>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              </MenuItem>
            )}
          </Select>
          {fetchError && tempModality === 'Cloud' && (
            <FormHelperText error>{fetchError}</FormHelperText>
          )}
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'rgba(0,0,0,0.7)' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: '#333',
            color: '#fff',
            '&:hover': { backgroundColor: '#444' },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
